import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { createMemoryRouter, RouterProvider } from 'react-router-dom';
import {
  routes,
  AppConfigProvider,
  AuthProvider,
  ApiProvider,
  ThemeProvider,
  RouteFallback,
  createMockAuth,
  useAuth,
  type Auth,
  type ApiError,
} from '@agnivo/app-core';
import { getMessages } from './launchCopy';
import { getBootLocale } from './bootLocale';
import { appConfig, authMode } from './config';
import { useTelegramInitDataAuth } from './auth/telegramInitDataAuth';
import {
  getTelegramWebApp,
  telegramSystemSchemeDetector,
  waitForTelegramWebApp,
  SDK_LIFECYCLE_WAIT_MS,
} from './telegram';
import { reportTguidMissing } from './telemetry/miniappTguidMissingSender';

/**
 * mini-app = a THIN shell over @agnivo/app-core (ADR-079), differing from web-ui
 * in exactly two seams:
 *  - the router factory: createMemoryRouter. Telegram OWNS the WebView URL
 *    fragment — it launches a Mini App by appending its payload there
 *    (`#tgWebAppData=…&tgWebAppVersion=…&tgWebAppStartParam=…`). A hash/URL
 *    router would read that fragment as a route path, match nothing, and render
 *    react-router's "Unexpected Application Error! 404" boundary (the
 *    2026-07-13 re-tap defect). We route in MEMORY; initData comes from the
 *    injected Telegram SDK object, with a READ-ONLY `#tgWebAppData` fragment
 *    fallback when the bridge yields nothing (telegram.ts, 2026-07-18 desktop
 *    class) — nothing ever ROUTES on or mutates the fragment.
 *    (start_param → initial route is a future follow-up — tracked at
 *     `active/2026-08-23-frontend-three-banked-follow-ups-in-app-core-and-mini-app.md`,
 *     finding #3, gated on a campaign needing a non-default landing screen.)
 *  - the auth provider: initData-exchange (ADR-082) instead of OIDC.
 */

// Same coherence guard web-ui owns: a live API with mock auth sends
// unauthenticated requests. For the mini-app the live-mode auth is 'telegram'.
if (appConfig.apiMode === 'live' && authMode === 'mock') {
  throw new Error('VITE_API_MODE=live requires VITE_AUTH_MODE=telegram (mock auth sends no bearer)');
}

// Built once (routes are static). Memory routing (NOT hash): the Telegram
// WebView URL fragment carries Telegram's launch payload, so the app must never
// derive its route from the URL — see the header note + test/router.test.ts.
const router = createMemoryRouter(routes);

/**
 * Re-render when a late `async` Telegram SDK lands.
 *
 * `getTelegramWebApp()` read during render is a ONE-SHOT. Since the SDK tag became
 * `async` (index.html, 2026-08-14) "absent" means "absent SO FAR", so a component
 * that reads it at render time and is never re-rendered stays pinned to the losing
 * side of that race for the whole session. The terminal-auth screen below reads it
 * TWICE — to gate «Закрыть» and to compose the TD-xx support code — and neither
 * read has anything to re-trigger it, because a terminal auth state is by
 * definition the end of that component's update stream.
 *
 * Bounded and self-cancelling on unmount. Outside Telegram (`pnpm dev`, a plain
 * browser) the poll simply times out and this stays false forever, which is the
 * correct answer there — the same no-op the pre-`async` synchronous read gave.
 */
function useTelegramSdkPresent(): boolean {
  const [present, setPresent] = useState(() => getTelegramWebApp() !== null);
  useEffect(() => {
    if (present) return;
    let cancelled = false;
    void waitForTelegramWebApp(SDK_LIFECYCLE_WAIT_MS).then((arrived) => {
      if (!cancelled && arrived) setPresent(true);
    });
    return () => {
      cancelled = true;
    };
  }, [present]);
  return present;
}

/**
 * Splash / retry gate over the initial initData exchange (telegram path only).
 *
 * THE `data-auth-state` ATTRIBUTES BELOW ARE LOAD-BEARING FOR CI — do not strip
 * them as unused markup. `quality-tests` browser-smoke `miniapp-launch.spec.ts`
 * asserts that no recovery-state marker is visible after a successful auth, via
 * `[data-state="recovery"], [data-recovery], [data-auth-state="failed"]`. Until
 * this commit the mini-app emitted none of those, so that assertion counted 0
 * unconditionally and was VACUOUSLY TRUE: it would have passed just as happily
 * with the recovery splash filling the screen. The spec says so itself and
 * carries a TODO for it — but the TODO is addressed to quality-tests, while the
 * missing half was always here, on the frontend.
 *
 * Three states, deliberately distinguishable:
 *  - `loading`  — the exchange is in flight. NOT matched by the smoke's failure
 *    selector, because being mid-flight is not a failure.
 *  - `failed` + `recovery`, `data-auth-terminal="true"`  — spent/tampered
 *    initData or a missing launch payload: no retry can succeed, only a relaunch.
 *    Carries `data-terminal-reason` with the classifier's own value, so a control
 *    can pin WHICH terminal rejection it reached rather than only THAT one was.
 *  - `failed` + `recovery`, `data-auth-terminal="false"` — transient; «Повторить»
 *    is live and can genuinely recover.
 *
 * The terminal/transient split is exposed because the two demand different user
 * remedies, so a smoke that cannot tell them apart cannot catch the defect where
 * one renders in the other's place — which is precisely the dead-«Повторить»
 * class this gate was built to prevent.
 *
 * NOTE: shipping these attributes does NOT by itself make the smoke's assertion
 * real. It removes the frontend half of the vacuity; the assertion stays
 * vacuous until quality-tests tightens it to REQUIRE the marker's presence on a
 * known-bad launch. Handed to qe — neither half works alone.
 */
export function LaunchGate({ children }: { children: ReactNode }) {
  const auth = useAuth();
  // The FROZEN boot locale, not a fresh detection. This component re-renders when the async
  // Telegram SDK arrives (that is what useTelegramSdkPresent is for), and detectLocale() answers
  // differently once it has: an EN-booted launch would flip to Russian copy under lang="en".
  // getBootLocale() is the single value main.tsx also wrote to the document — see bootLocale.ts.
  const t = getMessages(getBootLocale());
  // Unconditional (hook rules) and before every early return below. Cheap when
  // the SDK already won the race: `present` starts true and no poll is started.
  const sdkPresent = useTelegramSdkPresent();
  if (auth.status === 'loading') {
    return (
      <div
        data-auth-state="loading"
        className="flex h-full items-center justify-center p-6 text-center text-ink/70"
      >
        <p>{t.connecting}</p>
      </div>
    );
  }
  // In live mode a failed exchange must not fall through to API calls with no
  // bearer. In mock mode there's no host, so 'unauthenticated' is expected and
  // the mock transport needs no token — render through.
  if (auth.status === 'unauthenticated' && appConfig.apiMode === 'live') {
    // HONEST recovery (Dmitry #3): a SPENT/tampered initData (retryable===false)
    // can't recover by re-posting the same frozen blob — only a genuine relaunch
    // delivers fresh initData. So offer «Закрыть» (close → user reopens) instead
    // of a dead «Повторить». Transient/unknown (retryable !== false) keeps a
    // working retry. (The real seamless fix is the refresh RPC — developer, ADR-082.)
    if (auth.retryable === false) {
      // Per-reason terminal copy (B-AC-2: the remedy differs, so the copy does):
      // tg_uid-missing (pre-tg_uid session) and spent/forged initData both remedy
      // by RELAUNCH; no_launch_payload cannot — the client delivered no payload
      // at all (observed live: desktop Telegram, 2026-07-18), so the honest
      // remedy is a DIFFERENT Telegram surface, and no dead «Повторить».
      //
      // StoryBrand R5 (P0, S) — `expired` and `forged` now carry SEPARATE cause
      // labels even though they share the same remedy. The remediation (close +
      // relaunch from Telegram) is identical, so the second sentence is shared;
      // the cause label in the first sentence is what splits. Honest recovery
      // names what happened; "expired" is a false label for a forged payload and
      // was an honesty-floor violation. The product/native reviewer will refine
      // the Russian in a follow-up commit.
      const message =
        auth.terminalReason === 'tg_uid_missing'
          ? t.terminal.tgUidMissing
          : auth.terminalReason === 'no_launch_payload'
            ? t.terminal.noLaunchPayload(
                `TD-${sdkPresent ? 1 : 0}${window.location.hash.length > 1 ? 1 : 0}`,
              )
            : auth.terminalReason === 'forged'
              ? t.terminal.forged
              : t.terminal.expired;
      return (
        <div
          data-auth-state="failed"
          data-state="recovery"
          data-auth-terminal="true"
          // WHICH terminal rejection — the discriminator `data-auth-terminal`
          // alone cannot give. Same reason that attribute exists (web!327): a
          // marker every failure shares lets a control go green on the wrong
          // failure. quality-tests!546's bad-launch control posts a signed
          // `auth_date=0` blob and asserts the terminal marker; if fragment
          // routing regressed the gateway would see NO payload →
          // `no_launch_payload` → the same arm → the same marker, so the control
          // would pass while proving nothing about the freshness rejection it is
          // named for — green exactly when the launch path is broken.
          //
          // A PASS-THROUGH of the classifier's value, deliberately NOT derived
          // from the copy branch above. The two partition differently and that is
          // correct: `expired` and `forged` now carry distinct CAUSE labels but
          // share the same REMEDY, so four values (`expired` | `forged` |
          // `tg_uid_missing` | `no_launch_payload`) map to four messages (4×4).
          // Deriving the data attribute from `message` would re-collapse `expired`
          // and `forged` and re-open the hole one level down — the same
          // load-bearing reason this attribute exists.
          //
          // No `?? 'expired'` fallback, and that omission is load-bearing.
          // `retryable === false` implies `lastError !== null` (`isRetryable(null)`
          // returns `undefined`, never `false`), so this is non-null by
          // construction here and a default could only fire for a hand-built
          // `Auth`. Defaulting THAT to `expired` would let a reason-less terminal
          // state satisfy !546's tightened `[data-terminal-reason="expired"]` —
          // the same vacuity, reintroduced through the fallback. Absent instead:
          // React drops `undefined`, the selector fails to match, and the control
          // reds. Fail loud, not plausibly.
          data-terminal-reason={auth.terminalReason}
          className="flex h-full flex-col items-center justify-center gap-4 p-6 text-center"
        >
          <p className="text-ink/70">{message}</p>
          {/* «Закрыть» only when a Telegram bridge exists to close — a no-op
              button would recreate the dead-control defect this fixes. Gated on
              the hook, not a bare getTelegramWebApp(): under the `async` SDK tag
              a render-time read would hide the button for the whole session on
              any launch where the SDK lost the race. */}
          {sdkPresent && (
            <button
              type="button"
              className="text-accent underline"
              onClick={() => {
                // ADR-082 tg_uid-missing reopen surface (architect ruling
                // 2026-09-14). Gated on the SPECIFIC terminal reason, not fired
                // for every «Закрыть» click this button renders for (forged,
                // expired, no_launch_payload share this button too) — the
                // event's `step` dimension measures "of the users who saw the
                // tg_uid_missing error, how many clicked reopen", and an
                // ungated fire would mix unrelated terminal-failure clicks
                // into that ratio.
                if (auth.terminalReason === 'tg_uid_missing') {
                  reportTguidMissing({ step: 'reopen_clicked' });
                }
                getTelegramWebApp()?.close();
              }}
            >
              {t.close}
            </button>
          )}
        </div>
      );
    }
    return (
      <div
        data-auth-state="failed"
        data-state="recovery"
        data-auth-terminal="false"
        className="flex h-full flex-col items-center justify-center gap-4 p-6 text-center"
      >
        <p className="text-ink/70">{t.transientFailure}</p>
        <button type="button" className="text-accent underline" onClick={() => auth.login()}>
          {t.retry}
        </button>
      </div>
    );
  }
  return <>{children}</>;
}

/** Shared composition — same provider stack for both auth choices. */
function Shell({ auth, onApiError }: { auth: Auth; onApiError?: (err: ApiError) => void }) {
  return (
    <AppConfigProvider config={appConfig}>
      <ThemeProvider detector={telegramSystemSchemeDetector}>
        <AuthProvider auth={auth}>
          <LaunchGate>
            <ApiProvider onApiError={onApiError}>
              {/* fallbackElement covers the routes.tsx per-route lazy blank window
                during initial mount (web!598 product review). */}
            <RouterProvider router={router} fallbackElement={<RouteFallback />} />
            </ApiProvider>
          </LaunchGate>
        </AuthProvider>
      </ThemeProvider>
    </AppConfigProvider>
  );
}

function TelegramApp() {
  // onApiError routes a tg_uid-missing signal on any live call → the reopen surface
  const { auth, onApiError } = useTelegramInitDataAuth(appConfig.apiBaseUrl);
  return <Shell auth={auth} onApiError={onApiError} />;
}

function MockApp() {
  const auth = useMemo<Auth>(() => createMockAuth(), []);
  return <Shell auth={auth} />;
}

// authMode is a build-time constant, so each subtree calls its own hooks
// consistently — no conditional-hook violation.
export function App() {
  return authMode === 'telegram' ? <TelegramApp /> : <MockApp />;
}
